import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { Action } from 'typescript-fsa';

import { hideLoader, showLoader } from '@topconagriculture/loader';
import { getUserProfile } from '@topconagriculture/profiling';

import { loadMixersActions, selectActions } from '../../actions';
import api from '../../../core/api';
import { changeFarm, getFarmsListRequest, patchFarm, resetFarm } from './farms.actions';
import { LoadersNames } from '../../ui/ui.constants';
import { getFarms } from './farms.selector';
import { Farm } from './farms.types';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import { notificationError } from '../../../core/helpers/utils/notificationUtils';
import { SharingPartner, SharingRoles } from '../../../core/api/sharing/sharing.types';
import { UserRoles } from '../../user/user.types';
import { companyUserSelector } from '../../selectors';
import { successHandler } from '../../../core/helpers/utils/sagaUtils';
import { leaveInvitation } from '../sharing/sharing.actions';

export function* farmsListFetch() {
  yield put(showLoader(LoadersNames.fetchFarms));
  try {
    const result: Farm[] = yield call(api.farms.getFarmsList);
    const user = yield select(getUserProfile);
    const isSupport = yield select(companyUserSelector.isSupport);
    const sharedPartners = !isSupport && user.roles.includes(UserRoles.ADMIN) ? yield call(api.sharing.getShares) : [];
    const farmsExtended = result.map((elem) => {
      const partner = sharedPartners.find((share) => share.sender === elem.email && user.email !== elem.email);
      const role = partner ? (partner.metadata ? partner.metadata.role : SharingRoles.BASIC) : null;
      return { ...elem, isShared: elem.email !== user.email, sharedRole: role };
    });
    const userFarms = farmsExtended.filter((farm) => farm.companyId === user.company.id);
    const farm: Farm = userFarms[0];
    yield call(api.setFarmId, farm.id);
    yield put(selectActions.setCurrentFarm(farm.id));
    yield put(
      getFarmsListRequest.done({
        params: {},
        result: farmsExtended,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchFarms));
}

export function* getFarm(id: number) {
  try {
    return yield call(api.farms.getFarm, id);
  } catch (err) {
    throw err;
  }
}

export function* getSharedFarms() {
  try {
    const sharedPartners: SharingPartner[] = yield call(api.sharing.getShares);

    const shares = sharedPartners
      .filter((partner) => partner.metadata)
      .map((elem) => {
        return { id: elem.sharedItems[0].id, sharingRole: elem.metadata && elem.metadata.role };
      });
    const sharedfarms = yield all(
      shares.map((share) => {
        return call(getFarm, parseInt(share.id, 10));
      }),
    );

    return sharedfarms.map((farm, index) => {
      return { ...farm, isShared: true, sharedRole: shares[index].sharingRole };
    });
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

export function* changeFarmSaga(action: Action<string>) {
  const farmId = action.payload;
  const farms = yield select(getFarms);
  yield put(showLoader(LoadersNames.changeFarm));
  try {
    yield put(selectActions.resetSelected());
    yield put(loadMixersActions.resetLoadMixers());
    yield call(api.setFarmId, farmId);
    yield put(selectActions.setCurrentFarm(farmId));
    yield put(getFarmsListRequest.done({ params: {}, result: farms }));
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.changeFarm));
}

export function* patchFarmSaga(action: ReturnType<typeof patchFarm.started>) {
  yield put(showLoader(LoadersNames.patchFarm));
  try {
    const { data, formName } = action.payload;
    const result = yield call(api.farms.updateFarm, data, formName);
    yield put(
      patchFarm.done({
        params: action.payload,
        result,
      }),
    );
    if (action.meta && typeof action.meta.onSuccess === 'function') {
      action.meta.onSuccess();
    }
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.patchFarm));
}

export function* resetFarmSaga(action) {
  yield put(showLoader(LoadersNames.resetFarm));
  try {
    yield call(api.farms.resetFarm);
    yield put(selectActions.resetSelected());
    yield put(loadMixersActions.resetLoadMixers());
    yield put(getFarmsListRequest.started({}));
    successHandler(action.meta);
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.resetFarm));
}

export function* watchFarmsSaga() {
  yield all([
    yield takeLatest(getFarmsListRequest.started, farmsListFetch),
    yield takeLatest(leaveInvitation.done, farmsListFetch),
    yield takeLatest(changeFarm.type, changeFarmSaga),
    yield takeLatest(patchFarm.started, patchFarmSaga),
    yield takeLatest(resetFarm.type, resetFarmSaga),
  ]);
}
